import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import {
  HoldScreen,
  RequireActivityAccess,
  RequireAIAccess,
  RequireAuth,
  RequireBroadcastOverlayAccess,
  RequireHomeCard,
  RequireLeagueAdmin,
  RequireLeagueDataAccess,
  RequireLeagueManagement,
  RequireOnboarded,
  RequireSBotLabsAdmin,
  RequireStaffProfileAccess,
} from "./auth/guards";
import { AppShell } from "./components/AppShell";
import { AccountView } from "./features/account/AccountView";
import { SettingsView } from "./features/account/SettingsView";
import { AuthenticationView } from "./features/auth/AuthenticationView";
import { CompetitionCreation } from "./features/competitions/CompetitionCreation";
import { CompetitionDetail } from "./features/competitions/CompetitionDetail";
import { TemplatesConfig } from "./features/competitions/TemplatesConfig";
import { HomeDashboard } from "./features/home/HomeDashboard";
import { GpsMenu, OrganizationsList, RosterList, ScheduleList } from "./features/home/ListScreens";
import { LeagueAthleteConfig, LeagueOrgAdminConfig } from "./features/league/LeagueAthleteConfig";
import { LeagueExternalSources } from "./features/league/LeagueExternalSources";
import { LeagueHub } from "./features/league/LeagueHub";
import { LeagueInvitations } from "./features/league/LeagueInvitations";
import { LeagueSettings } from "./features/league/LeagueSettings";
import { OnboardingView } from "./features/onboarding/OnboardingView";
import { OrganizationCreate, OrganizationDetail } from "./features/organizations/OrganizationScreens";
import { AdminView } from "./features/admin/AdminView";
import { AthleteCreation } from "./features/league/AthleteCreation";
import { ActivityDetailView } from "./features/activity/ActivityDetailView";
import { ActivityListView } from "./features/activity/ActivityListView";
import { GpsAssignments } from "./features/gps/GpsAssignments";
import { GpsShareSettings } from "./features/gps/GpsShareSettings";
import { GpsData } from "./features/gps/GpsData";
import {
  AdminSupportDetailView,
  AdminSupportInboxView,
  UserSupportThreadView,
  UserSupportView,
} from "./features/support/SupportScreens";
import { LoadingView } from "./components/ui";
import { needsOnboarding } from "./models/user";
import { AppNavigationDeepLink } from "./features/ai/AppNavigationDeepLink";
import {
  NotificationDetailView,
  NotificationListView,
} from "./features/notifications/NotificationScreens";
import { ESPNAccessRequestAdminView } from "./features/admin/ESPNAccessRequestAdminView";
import { AthleteProfileView, StaffProfileView } from "./features/home/ProfileScreens";
import { AppEnvironmentScreen } from "./features/ai/AppEnvironmentScreen";

// Map-heavy screens are code-split so MapKit/togeojson stay out of the main bundle.
const GpsMap = lazy(() => import("./features/gps/GpsMap").then((m) => ({ default: m.GpsMap })));
const GpsHistory = lazy(() => import("./features/gps/GpsHistory").then((m) => ({ default: m.GpsHistory })));
const GpsDeviceDetail = lazy(() => import("./features/gps/GpsDeviceDetail").then((m) => ({ default: m.GpsDeviceDetail })));
// Public spectator GPS map (MapKit + KML land in this lazy chunk).
const GpsShareMap = lazy(() => import("./features/gps/GpsShareMap").then((m) => ({ default: m.GpsShareMap })));
const CourseViewer = lazy(() => import("./features/courses/CourseViewer").then((m) => ({ default: m.CourseViewer })));
const CourseEncoder = lazy(() => import("./features/courses/CourseEncoder").then((m) => ({ default: m.CourseEncoder })));
// Broadcast overlay surfaces (turf + togeojson land in these lazy chunks).
const OverlayPage = lazy(() => import("./overlay/OverlayPage").then((m) => ({ default: m.OverlayPage })));
const GlobalOverlayPage = lazy(() =>
  import("./overlay/GlobalOverlayPage").then((m) => ({ default: m.GlobalOverlayPage })),
);
const OverlayConfigList = lazy(() =>
  import("./features/overlays/OverlayConfigList").then((m) => ({ default: m.OverlayConfigList })),
);
const OverlayDesigner = lazy(() =>
  import("./features/overlays/OverlayDesigner").then((m) => ({ default: m.OverlayDesigner })),
);
// AI chat is code-split so the Axon packages + markdown renderer stay out of the main bundle.
const AIView = lazy(() => import("./features/ai/AIView").then((m) => ({ default: m.AIView })));
// Admin-only billing dashboard rarely loads — keep it out of the main bundle.
const BillingView = lazy(() => import("./features/admin/billing/BillingView").then((m) => ({ default: m.BillingView })));
const LeagueBillingAdminView = lazy(() =>
  import("./features/admin/LeagueBillingAdminView").then((m) => ({ default: m.LeagueBillingAdminView })),
);
const AIUsageAdminView = lazy(() =>
  import("./features/admin/AIUsageAdminView").then((m) => ({ default: m.AIUsageAdminView })),
);

function LazyScreen({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingView label="Loading…" />}>{children}</Suspense>;
}

// Route map mirrors the iOS screen hierarchy (RootView → tabs → drill-ins).

function RootRedirect() {
  const { isAuthenticated, isResolvingSession, currentUser } = useAuth();
  if (isResolvingSession) return <HoldScreen />;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (currentUser && needsOnboarding(currentUser)) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/home" replace />;
}

function AuthRoute() {
  const { isAuthenticated, isResolvingSession } = useAuth();
  if (isResolvingSession) return <HoldScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <AuthenticationView />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/auth" element={<AuthRoute />} />
      {/* Public broadcast overlay for OBS/TriCaster browser sources — no auth, no shell. */}
      <Route
        path="/overlay/:overlayId"
        element={
          <LazyScreen>
            <OverlayPage />
          </LazyScreen>
        }
      />
      {/* Public spectator GPS map — no auth, no shell; the feed is redacted server-side. */}
      <Route
        path="/share/gps/:shareId"
        element={
          <LazyScreen>
            <GpsShareMap />
          </LazyScreen>
        }
      />
      {/* Stable per-league output that forwards to the selected overlay. */}
      <Route
        path="/league-overlay/:outputId"
        element={
          <LazyScreen>
            <GlobalOverlayPage />
          </LazyScreen>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingView />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <RequireOnboarded>
              <AppShell />
            </RequireOnboarded>
          </RequireAuth>
        }
      >
        <Route path="/home" element={<AppEnvironmentScreen route="/home"><HomeDashboard /></AppEnvironmentScreen>} />
        <Route path="/app/*" element={<AppEnvironmentScreen route="/app/*"><AppNavigationDeepLink /></AppEnvironmentScreen>} />
        <Route
          path="/home/activity"
          element={
            <RequireActivityAccess>
              <AppEnvironmentScreen route="/home/activity">
                <ActivityListView />
              </AppEnvironmentScreen>
            </RequireActivityAccess>
          }
        />
        <Route
          path="/home/activity/:eventId"
          element={
            <RequireActivityAccess>
              <AppEnvironmentScreen route="/home/activity/:eventId">
                <ActivityDetailView />
              </AppEnvironmentScreen>
            </RequireActivityAccess>
          }
        />
        <Route
          path="/ai"
          element={
            <RequireAIAccess>
              <LazyScreen>
                <AIView />
              </LazyScreen>
            </RequireAIAccess>
          }
        />
        <Route path="/home/schedule" element={<RequireHomeCard cardID="leagueSchedule"><AppEnvironmentScreen route="/home/schedule"><ScheduleList /></AppEnvironmentScreen></RequireHomeCard>} />
        <Route path="/home/organizations" element={<RequireHomeCard cardID="organizations"><AppEnvironmentScreen route="/home/organizations"><OrganizationsList /></AppEnvironmentScreen></RequireHomeCard>} />
        <Route path="/home/athletes" element={<RequireHomeCard cardID="athletes"><AppEnvironmentScreen route="/home/athletes"><RosterList filter="athlete" /></AppEnvironmentScreen></RequireHomeCard>} />
        <Route path="/home/staff" element={<RequireHomeCard cardID="leagueStaff"><AppEnvironmentScreen route="/home/staff"><RosterList filter="staff" /></AppEnvironmentScreen></RequireHomeCard>} />
        <Route
          path="/home/athletes/:athleteID"
          element={
            <RequireHomeCard cardID="athletes">
              <AppEnvironmentScreen route="/home/athletes/:athleteID">
                <AthleteProfileView />
              </AppEnvironmentScreen>
            </RequireHomeCard>
          }
        />
        <Route
          path="/home/staff/:summaryID"
          element={
            <RequireHomeCard cardID="leagueStaff">
              <RequireStaffProfileAccess>
                <AppEnvironmentScreen route="/home/staff/:summaryID">
                  <StaffProfileView />
                </AppEnvironmentScreen>
              </RequireStaffProfileAccess>
            </RequireHomeCard>
          }
        />
        <Route path="/home/gps" element={<RequireHomeCard cardID="gpsTracking"><AppEnvironmentScreen route="/home/gps"><GpsMenu /></AppEnvironmentScreen></RequireHomeCard>} />

        <Route
          path="/league"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/league">
                <LeagueHub />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/course-encoder"
          element={
            <RequireLeagueManagement>
              <LazyScreen>
                <AppEnvironmentScreen route="/league/course-encoder">
                  <CourseEncoder />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/settings"
          element={
            <RequireLeagueAdmin>
              <AppEnvironmentScreen route="/league/settings">
                <LeagueSettings />
              </AppEnvironmentScreen>
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/league/invitations"
          element={
            <RequireLeagueAdmin>
              <AppEnvironmentScreen route="/league/invitations">
                <LeagueInvitations />
              </AppEnvironmentScreen>
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/league/athletes"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/league/athletes">
                <LeagueAthleteConfig />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/athletes/new"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/league/athletes/new">
                <AthleteCreation />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/external-sources"
          element={
            <RequireLeagueDataAccess>
              <AppEnvironmentScreen route="/league/external-sources">
                <LeagueExternalSources />
              </AppEnvironmentScreen>
            </RequireLeagueDataAccess>
          }
        />
        <Route
          path="/league/competitions"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/league/competitions">
                <TemplatesConfig />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/competitions/new"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/league/competitions/new">
                <CompetitionCreation />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/league/org-admin"
          element={
            <RequireLeagueAdmin>
              <AppEnvironmentScreen route="/league/org-admin">
                <LeagueOrgAdminConfig />
              </AppEnvironmentScreen>
            </RequireLeagueAdmin>
          }
        />

        <Route path="/competitions/:instanceId" element={<AppEnvironmentScreen route="/competitions/:instanceId"><CompetitionDetail /></AppEnvironmentScreen>} />
        <Route
          path="/competitions/:instanceId/courses"
          element={
            <LazyScreen>
              <AppEnvironmentScreen route="/competitions/:instanceId/courses">
                <CourseViewer />
              </AppEnvironmentScreen>
            </LazyScreen>
          }
        />
        <Route
          path="/competitions/:instanceId/gps/map"
          element={
            <LazyScreen>
              <AppEnvironmentScreen route="/competitions/:instanceId/gps/map">
                <GpsMap />
              </AppEnvironmentScreen>
            </LazyScreen>
          }
        />
        <Route
          path="/competitions/:instanceId/gps/assignments"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/competitions/:instanceId/gps/assignments">
                <GpsAssignments />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/competitions/:instanceId/gps/share"
          element={
            <RequireLeagueManagement>
              <AppEnvironmentScreen route="/competitions/:instanceId/gps/share">
                <GpsShareSettings />
              </AppEnvironmentScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/competitions/:instanceId/gps/devices/:deviceId"
          element={
            <RequireLeagueManagement>
              <LazyScreen>
                <AppEnvironmentScreen route="/competitions/:instanceId/gps/devices/:deviceId">
                  <GpsDeviceDetail />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireLeagueManagement>
          }
        />
        <Route
          path="/competitions/:instanceId/gps/history"
          element={
            <LazyScreen>
              <AppEnvironmentScreen route="/competitions/:instanceId/gps/history">
                <GpsHistory />
              </AppEnvironmentScreen>
            </LazyScreen>
          }
        />
        <Route path="/competitions/:instanceId/gps/data" element={<AppEnvironmentScreen route="/competitions/:instanceId/gps/data"><GpsData /></AppEnvironmentScreen>} />
        <Route
          path="/competitions/:instanceId/overlays"
          element={
            <RequireBroadcastOverlayAccess>
              <LazyScreen>
                <AppEnvironmentScreen route="/competitions/:instanceId/overlays">
                  <OverlayConfigList />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireBroadcastOverlayAccess>
          }
        />
        <Route
          path="/competitions/:instanceId/overlays/:overlayId"
          element={
            <RequireBroadcastOverlayAccess>
              <LazyScreen>
                <AppEnvironmentScreen route="/competitions/:instanceId/overlays/:overlayId">
                  <OverlayDesigner />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireBroadcastOverlayAccess>
          }
        />

        <Route path="/organizations/new" element={<AppEnvironmentScreen route="/organizations/new"><OrganizationCreate /></AppEnvironmentScreen>} />
        <Route path="/organizations/:organizationId" element={<AppEnvironmentScreen route="/organizations/:organizationId"><OrganizationDetail /></AppEnvironmentScreen>} />

        <Route path="/account" element={<AppEnvironmentScreen route="/account"><AccountView /></AppEnvironmentScreen>} />
        <Route path="/account/settings" element={<AppEnvironmentScreen route="/account/settings"><SettingsView /></AppEnvironmentScreen>} />
        <Route path="/account/notifications" element={<AppEnvironmentScreen route="/account/notifications"><NotificationListView /></AppEnvironmentScreen>} />
        <Route path="/account/notifications/:notificationID" element={<AppEnvironmentScreen route="/account/notifications/:notificationID"><NotificationDetailView /></AppEnvironmentScreen>} />
        <Route path="/account/support" element={<AppEnvironmentScreen route="/account/support"><UserSupportView /></AppEnvironmentScreen>} />
        <Route path="/account/support/:requestID" element={<AppEnvironmentScreen route="/account/support/:requestID"><UserSupportThreadView /></AppEnvironmentScreen>} />

        <Route
          path="/admin"
          element={
            <RequireSBotLabsAdmin>
              <AppEnvironmentScreen route="/admin">
                <AdminView />
              </AppEnvironmentScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/espn-access"
          element={
            <RequireSBotLabsAdmin>
              <AppEnvironmentScreen route="/admin/espn-access">
                <ESPNAccessRequestAdminView />
              </AppEnvironmentScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/support"
          element={
            <RequireSBotLabsAdmin>
              <AppEnvironmentScreen route="/admin/support">
                <AdminSupportInboxView />
              </AppEnvironmentScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/support/:requestID"
          element={
            <RequireSBotLabsAdmin>
              <AppEnvironmentScreen route="/admin/support/:requestID">
                <AdminSupportDetailView />
              </AppEnvironmentScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <RequireSBotLabsAdmin>
              <LazyScreen>
                <AppEnvironmentScreen route="/admin/billing">
                  <BillingView />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/league-billing"
          element={
            <RequireSBotLabsAdmin>
              <LazyScreen>
                <AppEnvironmentScreen route="/admin/league-billing">
                  <LeagueBillingAdminView />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireSBotLabsAdmin>
          }
        />
        <Route
          path="/admin/ai-usage"
          element={
            <RequireSBotLabsAdmin>
              <LazyScreen>
                <AppEnvironmentScreen route="/admin/ai-usage">
                  <AIUsageAdminView />
                </AppEnvironmentScreen>
              </LazyScreen>
            </RequireSBotLabsAdmin>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
